import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import "./MapListTrackerTool.css";
import {
  createTrackedList,
  deleteTrackedList,
  downloadTrackedListFile,
  listTrackedListVendors,
  listTrackedLists,
  setTrackedListStatus,
} from "../services/mapPolicyApi";
import { triggerZipDownload } from "../services/download";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "complete", label: "Complete" },
];

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

// Sentinel select value for free-text vendor entry.
const OTHER_VENDOR = "__other__";

const toolInfo = {
  toolName: "MAP List Tracker",
  description:
    "Track the vendor MAP lists you've worked on so you don't redo one by mistake. Log each list by vendor and mark it pending or complete. If you upload the same list again, you'll be warned before it's re-logged. This tool only tracks progress — it doesn't generate reports or change anything in Shopify.",
  steps: [
    "Pick the vendor the MAP list came from.",
    "Choose the MAP list file and click Log list.",
    "If you've already logged that exact file, you'll get a warning — confirm only if you really want to re-work it.",
    "If a different file reuses a name already tracked for that vendor, rename the file and upload it again.",
    "Mark a list Complete once you've generated the report and taken action.",
  ],
  templates: [],
};

const guideTips = [
  "“Complete” means you've done the work for that list (report generated + action taken) — you set it yourself.",
  "Re-sends are matched on the file's exact contents, not its name, so a renamed copy of the same file is still caught.",
  "An updated list (different contents) is logged as new — that's intentional, since the data changed. Give it a name that isn't already tracked for that vendor.",
  "File names must be unique per vendor: if a different file reuses a name already on file, rename it before uploading.",
  "Confirming a duplicate reopens that list as pending so it's back on your plate.",
];

function MapListTrackerTool() {
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const [vendors, setVendors] = useState([]); // [{ slug, label }]
  const [selectedVendor, setSelectedVendor] = useState("");
  const [customVendor, setCustomVendor] = useState(""); // free-text vendor name
  const [file, setFile] = useState(null);
  const [logging, setLogging] = useState(false);
  const [dupInfo, setDupInfo] = useState(null); // existing submission on a byte-identical 409
  const [nameConflict, setNameConflict] = useState(null); // {message, filename} on a filename clash 409
  const fileInputRef = useRef(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingItems, setLoadingItems] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadVendors = useCallback(async () => {
    try {
      const rows = await listTrackedListVendors();
      setVendors(rows.map((r) => ({ slug: r.slug, label: r.label })));
      setSelectedVendor((prev) => prev || rows[0]?.slug || "");
    } catch (error) {
      console.error(error);
      addToast("Failed to load vendors.", "error");
    }
  }, [addToast]);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const data = await listTrackedLists({
        vendorId: vendorFilter || undefined,
        status: statusFilter || undefined,
      });
      setItems(data.items || []);
      setPendingCount(data.pending || 0);
    } catch (error) {
      console.error(error);
      addToast("Failed to load tracked lists.", "error");
    } finally {
      setLoadingItems(false);
    }
  }, [vendorFilter, statusFilter, addToast]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Filter dropdown options: directory vendors ∪ any free-text vendors already
  // logged (so vendors added on the fly are still filterable). Keep the active
  // filter present even if the current page has no items for it.
  const filterVendors = useMemo(() => {
    const map = new Map();
    vendors.forEach((v) => map.set(v.slug, v.label || v.slug));
    items.forEach((it) => {
      if (it.vendorId && !map.has(it.vendorId)) {
        map.set(it.vendorId, it.vendorDisplayName || it.vendorId);
      }
    });
    if (vendorFilter && !map.has(vendorFilter)) map.set(vendorFilter, vendorFilter);
    return Array.from(map, ([slug, label]) => ({ slug, label })).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [vendors, items, vendorFilter]);

  const resetFileInput = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const doLog = async (force) => {
    const vendor =
      selectedVendor === OTHER_VENDOR ? customVendor.trim() : selectedVendor;
    if (!vendor) {
      addToast(
        selectedVendor === OTHER_VENDOR
          ? "Enter a vendor name."
          : "Pick a vendor first.",
        "error"
      );
      return;
    }
    if (!file) {
      addToast("Choose a MAP list file first.", "error");
      return;
    }
    setLogging(true);
    try {
      const { status, data } = await createTrackedList({
        vendor,
        file,
        force,
      });
      if (status === 409) {
        if (data.filenameConflict) {
          // A *different* file reuses a name already tracked for this vendor.
          // No reopen path — the buyer must rename and upload again.
          setDupInfo(null);
          setNameConflict({
            message:
              data.error ||
              "A file with this name is already tracked for this vendor. Please rename the file and upload again.",
            filename: data.submission?.filename || file.name,
          });
          return;
        }
        // Byte-identical re-send — surface the existing entry for confirmation.
        setNameConflict(null);
        setDupInfo(data.submission);
        return;
      }
      // 201 — logged (or reopened on force).
      if (force && data.duplicate) {
        addToast("List reopened as pending.", "success");
      } else {
        addToast("MAP list logged.", "success");
      }
      setDupInfo(null);
      setNameConflict(null);
      resetFileInput();
      setCustomVendor("");
      await loadItems();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to log list."), "error");
    } finally {
      setLogging(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const next = item.status === "complete" ? "pending" : "complete";
    setBusyId(item.id);
    try {
      await setTrackedListStatus(item.id, { status: next });
      addToast(`Marked ${next}.`, "success");
      await loadItems();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to update status."), "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (item) => {
    setBusyId(item.id);
    try {
      const { data, contentDisposition } = await downloadTrackedListFile(item.id);
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition || "");
      const filename = (match && decodeURIComponent(match[1])) || item.filename || "map_list";
      triggerZipDownload(data, filename);
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to download file."), "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        `Remove this tracked list (${item.filename || item.vendorDisplayName})? This only removes the tracking entry.`
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteTrackedList(item.id);
      addToast("Tracked list removed.", "success");
      await loadItems();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to remove."), "error");
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (item) => {
    const complete = item.status === "complete";
    return (
      <tr key={item.id}>
        <td className="mlt-cell-file">
          <span className="mlt-file-title">{item.filename || "—"}</span>
          {item.timesSeen > 1 ? (
            <span className="mlt-seen">seen {item.timesSeen}×</span>
          ) : null}
        </td>
        <td className="mlt-cell-vendor">{item.vendorDisplayName || item.vendorId}</td>
        <td>
          <span className={`mlt-status mlt-status-${item.status}`}>{item.status}</span>
          {complete ? (
            <span className="mlt-completed">Completed {fmtDate(item.completedAt)}</span>
          ) : null}
        </td>
        <td className="mlt-date">{fmtDate(item.firstSeenAt)}</td>
        <td className="mlt-date">
          {item.timesSeen > 1 ? fmtDate(item.lastSeenAt) : "—"}
        </td>
        <td className="mlt-actions">
          <span className="mlt-actions-inner">
            <button
              type="button"
              className={complete ? "mlt-act" : "mlt-act mlt-act-primary"}
              disabled={busyId === item.id}
              onClick={() => handleToggleStatus(item)}
            >
              {complete ? "Reopen" : "Mark complete"}
            </button>
            {item.hasFile ? (
              <button
                type="button"
                className="mlt-act"
                disabled={busyId === item.id}
                onClick={() => handleDownload(item)}
              >
                Download
              </button>
            ) : null}
            <button
              type="button"
              className="mlt-act mlt-act-danger"
              disabled={busyId === item.id}
              onClick={() => handleDelete(item)}
            >
              Remove
            </button>
          </span>
        </td>
      </tr>
    );
  };

  return (
    <>
      <ToolPageLayout
        title="MAP List Tracker"
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <div className="mlt">
          <section className="mlt-panel">
            <div className="mlt-panel-head">
              <h3 className="mlt-panel-title">Log a MAP list</h3>
            </div>
            <div className="mlt-panel-body">
              <div className="mlt-form">
                <div className="mlt-field">
                  <label className="tool-label" htmlFor="mlt-vendor">
                    Vendor
                  </label>
                  <select
                    id="mlt-vendor"
                    className="tool-input"
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                  >
                    {vendors.map((v) => (
                      <option key={v.slug} value={v.slug}>
                        {v.label || v.slug}
                      </option>
                    ))}
                    <option value={OTHER_VENDOR}>Other (type a name)…</option>
                  </select>
                  {selectedVendor === OTHER_VENDOR ? (
                    <input
                      type="text"
                      className="tool-input mlt-custom-vendor"
                      placeholder="New vendor name"
                      value={customVendor}
                      onChange={(e) => setCustomVendor(e.target.value)}
                    />
                  ) : null}
                </div>

                <div className="mlt-field">
                  <span className="tool-label">MAP list file</span>
                  <label className={`mlt-file-field${file ? " has-file" : ""}`}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="mlt-file-input"
                      accept=".xlsx,.xls,.csv,.pdf"
                      onChange={(e) => {
                        setFile(e.target.files?.[0] || null);
                        setNameConflict(null);
                      }}
                    />
                    <span className="mlt-file-btn">Choose file</span>
                    <span className="mlt-file-name">
                      {file ? file.name : "No file selected"}
                    </span>
                  </label>
                </div>

                <div className="mlt-field mlt-field-submit">
                  <button
                    type="button"
                    className="mlt-submit"
                    disabled={
                      logging ||
                      !file ||
                      (selectedVendor === OTHER_VENDOR
                        ? !customVendor.trim()
                        : !selectedVendor)
                    }
                    onClick={() => doLog(false)}
                  >
                    {logging ? "Logging…" : "Log list"}
                  </button>
                </div>
              </div>

              <p className="mlt-helper">
                The file is matched on its exact contents. Re-uploading a list you've already
                logged will warn you before re-logging it. Each file name must be unique per
                vendor — if a different file reuses a name already tracked, you'll be asked to
                rename it first.
              </p>

              {nameConflict ? (
                <div className="mlt-dup mlt-conflict">
                  <div className="mlt-dup-text">
                    {nameConflict.message}
                  </div>
                  <div className="mlt-dup-actions">
                    <button
                      type="button"
                      className="mlt-act"
                      onClick={() => {
                        setNameConflict(null);
                        resetFileInput();
                      }}
                    >
                      OK, I'll rename it
                    </button>
                  </div>
                </div>
              ) : null}

              {dupInfo ? (
                <div className="mlt-dup">
                  <div className="mlt-dup-text">
                    You already tracked this exact list for{" "}
                    <strong>{dupInfo.vendorDisplayName}</strong> on{" "}
                    <strong>{fmtDate(dupInfo.firstSeenAt)}</strong> — current status:{" "}
                    <strong>{dupInfo.status}</strong>
                    {dupInfo.filename ? <> (“{dupInfo.filename}”)</> : null}. Work on it again?
                  </div>
                  <div className="mlt-dup-actions">
                    <button
                      type="button"
                      className="mlt-submit"
                      disabled={logging}
                      onClick={() => doLog(true)}
                    >
                      Yes, reopen as pending
                    </button>
                    <button
                      type="button"
                      className="mlt-act"
                      disabled={logging}
                      onClick={() => {
                        setDupInfo(null);
                        resetFileInput();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="mlt-panel">
            <div className="mlt-panel-head">
              <div className="mlt-head-left">
                <h3 className="mlt-panel-title">Tracked lists</h3>
                {pendingCount ? (
                  <span className="mlt-count">{pendingCount} pending</span>
                ) : null}
              </div>
              <div className="mlt-filters">
                <select
                  className="tool-input mlt-filter"
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                >
                  <option value="">All vendors</option>
                  {filterVendors.map((v) => (
                    <option key={v.slug} value={v.slug}>
                      {v.label || v.slug}
                    </option>
                  ))}
                </select>
                <select
                  className="tool-input mlt-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_FILTERS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mlt-panel-body mlt-panel-body--flush">
              {loadingItems ? (
                <p className="mlt-empty">Loading…</p>
              ) : items.length === 0 ? (
                <p className="mlt-empty">
                  No tracked lists{statusFilter ? ` (${statusFilter})` : ""} yet. Log a MAP list
                  above to start tracking.
                </p>
              ) : (
                <div className="mlt-table-wrap">
                  <table className="mlt-table">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Vendor</th>
                        <th>Status</th>
                        <th>Logged</th>
                        <th>Last seen</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>{items.map(renderRow)}</tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default MapListTrackerTool;
